'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function RepaymentCalculator() {
  const t = useTranslations("tools.repaymentCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    loanAmount: 10000,
    interestRate: 10,
    compoundFrequency: 12,
    paymentFrequency: 12,
    repaymentType: 'term',
    years: 5,
    months: 0,
    paymentAmount: 200
  });

  const [results, setResults] = useState<any>(null);
  const [showAmortization, setShowAmortization] = useState(false);
  const [amortizationSchedule, setAmortizationSchedule] = useState<any[]>([]);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#f59e0b',
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    border: '#e2e8f0',
    background: '#f8fafc',
    cardBg: '#ffffff',
  };

  const getPaymentLabel = (freq: number) => {
    const labels: { [key: number]: string } = {
      365: t('dailyPayment'),
      52: t('weeklyPayment'),
      26: t('biweeklyPayment'),
      24: t('semimonthlyPayment'),
      12: t('monthlyPayment'),
      4: t('quarterlyPayment'),
      2: t('semiannualPayment'),
      1: t('annualPayment')
    };
    return labels[freq] || t('paymentAmount');
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { loanAmount, interestRate, compoundFrequency, paymentFrequency, repaymentType, years, months, paymentAmount } = inputs;

    const annualRate = interestRate / 100;
    const periodicRate = annualRate / compoundFrequency;
    const paymentsPerCompound = compoundFrequency / paymentFrequency;
    const effectiveRate = Math.pow(1 + periodicRate, paymentsPerCompound) - 1;

    let payment: number, totalPayments: number, totalPaid: number, totalInterest: number;
    let primaryLabel: string, primaryValue: string, secondaryLabel: string, secondaryValue: string;

    if (repaymentType === 'term') {
      totalPayments = years * paymentFrequency + Math.round(months * paymentFrequency / 12);

      if (totalPayments === 0) {
        alert(t('validTermError'));
        return;
      }

      if (effectiveRate === 0) {
        payment = loanAmount / totalPayments;
      } else {
        payment = loanAmount * (effectiveRate * Math.pow(1 + effectiveRate, totalPayments)) /
                 (Math.pow(1 + effectiveRate, totalPayments) - 1);
      }

      totalPaid = payment * totalPayments;
      totalInterest = totalPaid - loanAmount;

      primaryLabel = getPaymentLabel(paymentFrequency);
      primaryValue = '$' + payment.toFixed(2);
      secondaryLabel = t('totalPayments');
      secondaryValue = totalPayments.toString();

    } else {
      payment = paymentAmount;

      if (payment <= 0) {
        alert(t('validPaymentError'));
        return;
      }

      if (effectiveRate === 0) {
        totalPayments = Math.ceil(loanAmount / payment);
      } else {
        const minPayment = loanAmount * effectiveRate;
        if (payment <= minPayment) {
          alert(t('paymentTooLowError') + ' $' + minPayment.toFixed(2));
          return;
        }
        totalPayments = Math.ceil(Math.log(payment / (payment - loanAmount * effectiveRate)) /
                                  Math.log(1 + effectiveRate));
      }

      totalPaid = payment * totalPayments;
      totalInterest = totalPaid - loanAmount;

      const calcYears = Math.floor(totalPayments / paymentFrequency);
      const remainingPayments = totalPayments % paymentFrequency;
      const calcMonths = Math.round(remainingPayments * 12 / paymentFrequency);

      let termText = '';
      if (calcYears > 0) termText += calcYears + ' ' + (calcYears > 1 ? t('years') : t('year'));
      if (calcMonths > 0) {
        if (termText) termText += ' ';
        termText += calcMonths + ' ' + (calcMonths > 1 ? t('months') : t('month'));
      }

      primaryLabel = t('payoffTime');
      primaryValue = termText || totalPayments + ' ' + t('payments');
      secondaryLabel = t('totalPayments');
      secondaryValue = totalPayments.toString();
    }

    const principalPercent = Math.round((loanAmount / totalPaid) * 100);
    const interestPercent = 100 - principalPercent;

    // Generate amortization schedule
    const schedule = generateAmortizationSchedule(loanAmount, payment, effectiveRate, totalPayments);
    setAmortizationSchedule(schedule);

    setResults({
      primaryLabel,
      primaryValue,
      secondaryLabel,
      secondaryValue,
      totalPaid: '$' + totalPaid.toFixed(2),
      totalInterest: '$' + totalInterest.toFixed(2),
      principalPercent,
      interestPercent,
      loanAmount
    });
  };

  const generateAmortizationSchedule = (principal: number, payment: number, rate: number, totalPayments: number) => {
    let balance = principal;
    const schedule = [];
    const maxRows = Math.min(totalPayments, 120);

    for (let i = 1; i <= maxRows; i++) {
      const interest = balance * rate;
      const principalPaid = Math.min(payment - interest, balance);
      balance -= principalPaid;

      if (balance < 0.01) balance = 0;

      schedule.push({
        payment: i,
        paymentAmount: payment.toFixed(2),
        principal: principalPaid.toFixed(2),
        interest: interest.toFixed(2),
        balance: balance.toFixed(2)
      });

      if (balance === 0) break;
    }

    return schedule;
  };

  const drawPieChart = () => {
    if (!results) return null;

    const { principalPercent, interestPercent } = results;
    const radius = 80;
    const cx = 100;
    const cy = 100;

    const principalAngle = (principalPercent / 100) * 360;

    const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
      const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
      return {
        x: centerX + (radius * Math.cos(angleInRadians)),
        y: centerY + (radius * Math.sin(angleInRadians))
      };
    };

    const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
      const start = polarToCartesian(x, y, radius, endAngle);
      const end = polarToCartesian(x, y, radius, startAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

      return [
        'M', x, y,
        'L', start.x, start.y,
        'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y,
        'Z'
      ].join(' ');
    };

    const principalPath = describeArc(cx, cy, radius, 0, principalAngle);
    const interestPath = describeArc(cx, cy, radius, principalAngle, 360);

    return (
      <svg width="200" height="200" style={{ display: 'block', margin: '0 auto' }}>
        <path d={principalPath} fill={colors.primary} />
        <path d={interestPath} fill={colors.secondary} />
        <circle cx={cx} cy={cy} r={30} fill="white" />
      </svg>
    );
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '1000px', margin: '0 auto' },
    {},
    {},
    {}
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    {}
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorLayoutStyle: any = {
    display: 'grid',
    gap: '2rem',
    marginTop: '2rem',
    ...(screenSize === 'desktop' ? { gridTemplateColumns: '1fr 1fr' } : { gridTemplateColumns: '1fr' })
  };

  const formCardStyle = getResponsiveStyle(
    {
      background: colors.cardBg,
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = { marginBottom: '1.5rem' };

  const labelStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 600, color: colors.textSecondary, marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: `2px solid ${colors.border}`,
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const selectStyle = { ...inputStyle };

  const inputWithPrefixStyle = { position: 'relative' as const };

  const inputPrefixStyle = {
    position: 'absolute' as const,
    left: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: colors.textSecondary,
    fontWeight: 600
  };

  const inputSuffixStyle = {
    position: 'absolute' as const,
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: colors.textSecondary,
    fontWeight: 600
  };

  const repaymentOptionsStyle = {
    background: colors.background,
    padding: '1rem',
    borderRadius: '0.5rem',
    marginBottom: '1.5rem'
  };

  const radioGroupStyle = { marginBottom: '1rem' };

  const radioLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontWeight: 500,
    color: colors.textSecondary
  };

  const termInputsStyle: any = {
    display: 'grid',
    gap: '1rem',
    ...(screenSize === 'mobile' ? { gridTemplateColumns: '1fr' } : { gridTemplateColumns: '1fr 1fr' })
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: `0 4px 6px -1px rgba(37, 99, 235, 0.3)`
    },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1.1rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const resultsCardStyle = formCardStyle;

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    marginBottom: '0.75rem',
    background: colors.background,
    borderRadius: '0.5rem',
    borderLeft: `4px solid ${colors.border}`
  };

  const resultItemPrimaryStyle = getResponsiveStyle(
    {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
      borderRadius: '0.5rem',
      borderLeft: `4px solid ${colors.primary}`,
      marginBottom: '0.75rem'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.25rem' }
  );

  const resultLabelStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textSecondary },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary },
    { fontSize: '1.1rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const resultValuePrimaryStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.primary },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '1.75rem' }
  );

  const chartContainerStyle = { marginTop: '2rem', textAlign: 'center' as const };

  const chartLegendStyle: any = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '1rem',
    ...(screenSize === 'mobile' ? { flexDirection: 'column' as const, alignItems: 'center', gap: '0.75rem' } : { gap: '2rem' })
  };

  const legendItemStyle = { display: 'flex', alignItems: 'center', gap: '0.5rem' };

  const legendColorStyle = { width: '1rem', height: '1rem', borderRadius: '0.25rem' };

  const legendTextStyle = { fontSize: '0.9rem', color: colors.textSecondary };

  const toggleBtnStyle = {
    background: 'none',
    border: `2px solid ${colors.primary}`,
    color: colors.primary,
    padding: '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  };

  const amortizationTableStyle = { marginTop: '1.5rem', overflowX: 'auto' as const };

  const tableStyle = getResponsiveStyle(
    { width: '100%', borderCollapse: 'collapse' as const },
    { fontSize: '0.8rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = { ...paragraphStyle, paddingLeft: '1.5rem' };

  const listItemStyle = { marginBottom: '0.75rem' };

  const strongStyle = { fontWeight: 600, color: colors.textPrimary };

  const refLinkStyle = { color: colors.primary, textDecoration: 'none', wordBreak: 'break-all' as const };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t('toolTitle')}</h2>
          </div>

          <div style={calculatorLayoutStyle}>
            <div style={formCardStyle}>
              <form onSubmit={handleCalculate}>
                <div style={formGroupStyle}>
                  <label htmlFor="loan-amount" style={labelStyle}>{t('loanBalance')}</label>
                  <div style={inputWithPrefixStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="loan-amount"
                      step="0.01"
                      min="0"
                      value={inputs.loanAmount}
                      onChange={(e) => setInputs({...inputs, loanAmount: parseFloat(e.target.value) || 0})}
                      style={{...inputStyle, paddingLeft: '2rem'}}
                      required
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="interest-rate" style={labelStyle}>{t('interestRate')}</label>
                  <div style={inputWithPrefixStyle}>
                    <input
                      type="number"
                      id="interest-rate"
                      step="0.01"
                      min="0"
                      max="100"
                      value={inputs.interestRate}
                      onChange={(e) => setInputs({...inputs, interestRate: parseFloat(e.target.value) || 0})}
                      style={{...inputStyle, paddingRight: '2rem'}}
                      required
                    />
                    <span style={inputSuffixStyle}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="compound" style={labelStyle}>{t('compoundFrequency')}</label>
                  <select
                    id="compound"
                    value={inputs.compoundFrequency}
                    onChange={(e) => setInputs({...inputs, compoundFrequency: parseInt(e.target.value)})}
                    style={selectStyle}
                  >
                    <option value="12">{t('monthlyAPR')}</option>
                    <option value="365">{t('daily')}</option>
                    <option value="52">{t('weekly')}</option>
                    <option value="26">{t('biweekly')}</option>
                    <option value="24">{t('semimonthly')}</option>
                    <option value="4">{t('quarterly')}</option>
                    <option value="2">{t('semiannually')}</option>
                    <option value="1">{t('annuallyAPY')}</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="payment-frequency" style={labelStyle}>{t('paymentFrequency')}</label>
                  <select
                    id="payment-frequency"
                    value={inputs.paymentFrequency}
                    onChange={(e) => setInputs({...inputs, paymentFrequency: parseInt(e.target.value)})}
                    style={selectStyle}
                  >
                    <option value="12">{t('everyMonth')}</option>
                    <option value="365">{t('everyDay')}</option>
                    <option value="52">{t('everyWeek')}</option>
                    <option value="26">{t('every2Weeks')}</option>
                    <option value="24">{t('everyHalfMonth')}</option>
                    <option value="4">{t('everyQuarter')}</option>
                    <option value="2">{t('every6Months')}</option>
                    <option value="1">{t('everyYear')}</option>
                  </select>
                </div>

                <div style={repaymentOptionsStyle}>
                  <div style={radioGroupStyle}>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="repayment-type"
                        value="term"
                        checked={inputs.repaymentType === 'term'}
                        onChange={(e) => setInputs({...inputs, repaymentType: e.target.value})}
                        style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                      />
                      {t('repayFixedTime')}
                    </label>
                    {inputs.repaymentType === 'term' && (
                      <div style={{ marginLeft: '1.5rem', marginTop: '0.75rem' }}>
                        <div style={termInputsStyle}>
                          <div style={formGroupStyle}>
                            <label htmlFor="years" style={labelStyle}>{t('years')}</label>
                            <input
                              type="number"
                              id="years"
                              min="0"
                              value={inputs.years}
                              onChange={(e) => setInputs({...inputs, years: parseInt(e.target.value) || 0})}
                              style={inputStyle}
                            />
                          </div>
                          <div style={formGroupStyle}>
                            <label htmlFor="months" style={labelStyle}>{t('months')}</label>
                            <input
                              type="number"
                              id="months"
                              min="0"
                              max="11"
                              value={inputs.months}
                              onChange={(e) => setInputs({...inputs, months: parseInt(e.target.value) || 0})}
                              style={inputStyle}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={radioGroupStyle}>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="repayment-type"
                        value="amount"
                        checked={inputs.repaymentType === 'amount'}
                        onChange={(e) => setInputs({...inputs, repaymentType: e.target.value})}
                        style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                      />
                      {t('repayFixedInstallment')}
                    </label>
                    {inputs.repaymentType === 'amount' && (
                      <div style={{ marginLeft: '1.5rem', marginTop: '0.75rem' }}>
                        <div style={formGroupStyle}>
                          <label htmlFor="payment-amount" style={labelStyle}>{t('paymentAmount')}</label>
                          <div style={inputWithPrefixStyle}>
                            <span style={inputPrefixStyle}>$</span>
                            <input
                              type="number"
                              id="payment-amount"
                              step="0.01"
                              min="0"
                              value={inputs.paymentAmount}
                              onChange={(e) => setInputs({...inputs, paymentAmount: parseFloat(e.target.value) || 0})}
                              style={{...inputStyle, paddingLeft: '2rem'}}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
              </form>
            </div>

            {results && (
              <div style={resultsCardStyle}>
                <h3 style={h2Style}>{t('resultsTitle')}</h3>

                <div style={resultItemPrimaryStyle}>
                  <span style={resultLabelStyle}>{results.primaryLabel}</span>
                  <span style={resultValuePrimaryStyle}>{results.primaryValue}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{results.secondaryLabel}</span>
                  <span style={resultValueStyle}>{results.secondaryValue}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('totalAmountPaid')}</span>
                  <span style={resultValueStyle}>{results.totalPaid}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('totalInterest')}</span>
                  <span style={resultValueStyle}>{results.totalInterest}</span>
                </div>

                <div style={chartContainerStyle}>
                  {drawPieChart()}
                  <div style={chartLegendStyle}>
                    <div style={legendItemStyle}>
                      <div style={{...legendColorStyle, background: colors.primary}}></div>
                      <span style={legendTextStyle}>{t('principal')} ({results.principalPercent}%)</span>
                    </div>
                    <div style={legendItemStyle}>
                      <div style={{...legendColorStyle, background: colors.secondary}}></div>
                      <span style={legendTextStyle}>{t('interest')} ({results.interestPercent}%)</span>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem', textAlign: 'center' as const }}>
                  <button
                    onClick={() => setShowAmortization(!showAmortization)}
                    style={toggleBtnStyle}
                  >
                    {showAmortization ? t('hideAmortization') : t('viewAmortization')}
                  </button>
                </div>

                {showAmortization && (
                  <div style={amortizationTableStyle}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `1px solid ${colors.border}`, background: colors.background, fontWeight: 600, color: colors.textSecondary }}>{t('paymentNum')}</th>
                          <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `1px solid ${colors.border}`, background: colors.background, fontWeight: 600, color: colors.textSecondary }}>{t('payment')}</th>
                          <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `1px solid ${colors.border}`, background: colors.background, fontWeight: 600, color: colors.textSecondary }}>{t('principal')}</th>
                          <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `1px solid ${colors.border}`, background: colors.background, fontWeight: 600, color: colors.textSecondary }}>{t('interest')}</th>
                          <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `1px solid ${colors.border}`, background: colors.background, fontWeight: 600, color: colors.textSecondary }}>{t('balance')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {amortizationSchedule.map((row, index) => (
                          <tr key={index} style={{ background: index % 2 === 0 ? 'transparent' : colors.background }}>
                            <td style={{ padding: '0.75rem', borderBottom: `1px solid ${colors.border}` }}>{row.payment}</td>
                            <td style={{ padding: '0.75rem', borderBottom: `1px solid ${colors.border}` }}>${row.paymentAmount}</td>
                            <td style={{ padding: '0.75rem', borderBottom: `1px solid ${colors.border}` }}>${row.principal}</td>
                            <td style={{ padding: '0.75rem', borderBottom: `1px solid ${colors.border}` }}>${row.interest}</td>
                            <td style={{ padding: '0.75rem', borderBottom: `1px solid ${colors.border}` }}>${row.balance}</td>
                          </tr>
                        ))}
                        {amortizationSchedule.length === 120 && (
                          <tr>
                            <td colSpan={5} style={{ padding: '0.75rem', textAlign: 'center', color: colors.textSecondary, fontStyle: 'italic' }}>
                              {t('showingFirst120')}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step5Title')}</strong> {t('step5Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step6Title')}</strong> {t('step6Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight1Title')}</strong> {t('insight1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight2Title')}</strong> {t('insight2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight3Title')}</strong> {t('insight3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight4Title')}</strong> {t('insight4Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.indiatimes.com/partner/top-features-to-look-for-in-a-home-loan-calculator-in-2025/articleshow/122728784.html" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.kff.org/interactive/calculator-aca-enhanced-premium-tax-credit/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.mortgagecalculator.org" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.nerdwallet.com/personal-loans/calculators/loan-payment" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://studentaid.gov/loan-simulator" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref5')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://finaid.org/calculators/prepayment/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref6')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.mortgagecalculator.net" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref7')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}